// 游戏类型定义
export type Player = 'black' | 'white';
export type PlayerColor = Player; // 玩家选择的颜色，用于人机对战
export type XiangqiPlayer = 'black' | 'red'; // 中国象棋专用玩家类型
export type CellState = Player | null;
export type GameStatus = 'playing' | 'finished' | 'paused';
export type GameMode = 'pvp' | 'pvc'; // pvp: 双人对战, pvc: 人机对战
export type Difficulty = 'easy' | 'medium' | 'hard';
export type GameType = 'gomoku' | 'reversi' | 'checkers' | 'jungle' | 'xiangqi' | 'tigerchicken' | 'tictactoe' | 'match3' | 'connectfour' | 'hnefatafl' | 'straight' | 'tablut'; // gomoku: 五子棋, reversi: 黑白棋, checkers: 国际跳棋, jungle: 斗兽棋, xiangqi: 中国象棋, tigerchicken: 虎鸡虫棒棋, tictactoe: 井字棋, match3: 消消看, connectfour: 四子棋, hnefatafl: 维京板棋, straight: 直棋, tablut: 萨米板棋

export interface Position {
  row: number;
  col: number;
}

export interface Move {
  position: Position;
  player: Player;
  timestamp: number;
  skillUsed?: string;
}

export interface GameState<TBoard = any, TMoves = Move> {
  board: TBoard;
  currentPlayer: Player;
  status: GameStatus;
  winner: Player | null;
  moves: TMoves[];
  energy: { black: number; white: number };
  skills: { black: Skill[]; white: Skill[] };
  gameHistory: GameState<TBoard, TMoves>[];
  gameMode: GameMode;
  gameType: GameType; // 游戏类型
  difficulty?: Difficulty; // 仅在人机对战时使用
  isAIThinking?: boolean; // AI思考状态，用于UI显示
  // 黑白棋特有状态
  validMoves?: Position[]; // 当前玩家可以下棋的位置
  lastFlippedPieces?: Position[]; // 最后一步翻转的棋子位置
  passCount?: number; // 连续跳过回合次数（用于检测游戏结束）
  blackScore?: number; // 黑棋数量
  whiteScore?: number; // 白棋数量
  // 国际跳棋特有状态
  selectedPiece?: Position; // 当前选中的棋子
  possibleMoves?: CheckersMove[]; // 选中棋子的可能移动 - 使用CheckersMove以支持跳棋
  mustCapture?: boolean; // 是否必须吃子
  captureSequence?: Position[]; // 连续吃子的路径
  blackPiecesCount?: number; // 黑棋剩余数量
  whitePiecesCount?: number; // 白棋剩余数量
  blackKingsCount?: number; // 黑王数量
  whiteKingsCount?: number; // 白王数量
}

export interface Skill {
  id: string;
  name: string;
  description: string;
  energyCost: number;
  cooldown: number;
  cooldownRemaining: number;
  timesUsed: number;
  type: 'traditional' | 'modern';
  effect: (gameState: GameState, target?: Position) => GameState;
  icon: string;
}

export interface GameConfig {
  boardSize: number;
  initialEnergy: number;
  energyPerTurn: number;
  winCondition: number;
  gameType: GameType;
}

// ==================== 国际跳棋相关接口 ====================
// 国际跳棋棋子类型
export type CheckersPieceType = 'man' | 'king'; // man: 普通棋子, king: 王棋

// 国际跳棋棋子
export interface CheckersPiece {
  player: Player;
  type: CheckersPieceType;
}

// 国际跳棋移动
export interface CheckersMove {
  player: Player;           // 执行移动的玩家
  from: Position;           // 起始位置
  to: Position;             // 目标位置
  captures?: Position[];    // 被吃掉的棋子位置列表
  isKingMove?: boolean;     // 是否为王棋移动
  isPromotion?: boolean;    // 是否升级为王棋
}

// 国际跳棋游戏状态
export interface CheckersGameState extends GameState<(CheckersPiece | null)[][], CheckersMove> {
  // 基础游戏状态
  selectedPiece?: Position;           // 当前选中的棋子位置
  possibleMoves: CheckersMove[];      // 当前可能的移动列表
  mustCapture: boolean;               // 是否必须吃子
  captureSequence?: Position[];      // 连续吃子序列
  isCapturing: boolean;               // 是否正在连续吃子
  lastCapture?: Position;           // 上次吃子的位置

  // 棋子计数
  blackPiecesCount: number;          // 黑方棋子总数
  whitePiecesCount: number;          // 白方棋子总数
  blackKingsCount: number;           // 黑方王棋数量
  whiteKingsCount: number;           // 白方王棋数量

  // 游戏统计
  totalMoves: number;                // 总移动数
  captureMoves: number;              // 吃子移动数
  promotionMoves: number;            // 升级移动数
}

// 黑白棋配置
export const REVERSI_CONFIG: GameConfig = {
  boardSize: 8,
  initialEnergy: 0, // 黑白棋不使用能量系统
  energyPerTurn: 0,
  winCondition: 0, // 黑白棋通过棋子数量决定胜负
  gameType: 'reversi'
};

// 五子棋配置
export const GOMOKU_CONFIG: GameConfig = {
  boardSize: 15,
  initialEnergy: 3,
  energyPerTurn: 1,
  winCondition: 5,
  gameType: 'gomoku'
};

// 国际跳棋配置 (10×10百格跳棋 - 国际正式比赛规则)
export const CHECKERS_CONFIG: GameConfig = {
  boardSize: 10,                  // 10×10棋盘
  initialEnergy: 0,               // 不使用能量系统
  energyPerTurn: 0,
  winCondition: 0,                // 通过吃光对手棋子获胜
  gameType: 'checkers'
};

// ==================== 斗兽棋相关接口 ====================
// 动物类型
export type AnimalType = 'elephant' | 'lion' | 'tiger' | 'leopard' | 'wolf' | 'dog' | 'cat' | 'mouse';

// 地形类型
export type TerrainType = 'normal' | 'river' | 'trap' | 'den';

// 斗兽棋动物
export interface JungleAnimal {
  player: Player;
  type: AnimalType;
  rank: number;  // 动物等级，1-8，数字越大越强
}

// 斗兽棋地形
export interface JungleTerrain {
  type: TerrainType;
  owner?: Player;  // 陷阱和巢穴的归属
}

// 斗兽棋移动
export interface JungleMove {
  player: Player;
  from: Position;
  to: Position;
  captures?: JungleAnimal[];
  isRiverJump?: boolean;  // 是否是跳河移动
}

// 斗兽棋游戏状态
export interface JungleGameState {
  // 棋盘信息
  board: (JungleAnimal | null)[][];
  terrain: JungleTerrain[][];

  // 地形信息
  traps: { black: Position[], white: Position[] };
  dens: { black: Position, white: Position };
  riverPositions: Position[];

  // 动物信息
  animals: { black: JungleAnimal[], white: JungleAnimal[] };
  capturedAnimals: { black: JungleAnimal[], white: JungleAnimal[] };

  // 游戏状态
  currentPlayer: Player;
  selectedPiece?: Position;
  possibleMoves?: JungleMove[];
  status: GameStatus;
  winner?: Player;
  isAIThinking?: boolean;

  // 游戏历史和统计
  moves: JungleMove[];
  gameHistory: JungleGameState[];
  totalMoves: number;
  captureMoves: number;
  riverJumps: number;
}

// 斗兽棋配置 (7×9棋盘 - 斗兽棋规则)
export const JUNGLE_CONFIG: GameConfig = {
  boardSize: 7,                   // 7×9棋盘（7列9行）
  initialEnergy: 0,               // 不使用能量系统
  energyPerTurn: 0,
  winCondition: 0,                // 占领对方巢穴或吃光对方棋子
  gameType: 'jungle'
};

// 动物等级映射
export const ANIMAL_RANKS: Record<AnimalType, number> = {
  elephant: 8,  // 象
  lion: 7,      // 狮
  tiger: 6,     // 虎
  leopard: 5,   // 豹
  wolf: 4,      // 狼
  dog: 3,       // 狗
  cat: 2,       // 猫
  mouse: 1      // 鼠
};

// 动物名称映射
export const ANIMAL_NAMES: Record<AnimalType, string> = {
  elephant: '象',
  lion: '狮',
  tiger: '虎',
  leopard: '豹',
  wolf: '狼',
  dog: '狗',
  cat: '猫',
  mouse: '鼠'
};

// 动物表情符号
export const ANIMAL_EMOJIS: Record<AnimalType, string> = {
  elephant: '🐘',
  lion: '🦁',
  tiger: '🐅',
  leopard: '🐆',
  wolf: '🐺',
  dog: '🐕',
  cat: '🐈',
  mouse: '🐭'
};

// ==================== 中国象棋类型定义 ====================

// 中国象棋棋子类型
export type XiangqiPieceType =
  | 'general'  // 将/帅
  | 'advisor'  // 士
  | 'elephant' // 象/相
  | 'horse'    // 马
  | 'chariot'  // 车
  | 'cannon'   // 炮
  | 'soldier'; // 兵/卒

// 中国象棋棋子
export interface XiangqiPiece {
  type: XiangqiPieceType;
  player: XiangqiPlayer;
  hasMoved?: boolean; // 是否已经移动过
}

// 中国象棋移动
export interface XiangqiMove {
  player: XiangqiPlayer;
  from: Position;
  to: Position;
  captures?: XiangqiPiece; // 被吃的棋子
  isCheck?: boolean; // 是否将军
  isCheckmate?: boolean; // 是否将死
  isCastle?: boolean; // 是否为王车易位（虽然象棋中没有）
  isEnPassant?: boolean; // 是否是吃过路兵（虽然象棋中没有）
}

// 中国象棋游戏状态
export interface XiangqiGameState {
  // 基础游戏状态
  board: (XiangqiPiece | null)[][];
  currentPlayer: XiangqiPlayer;
  status: 'playing' | 'finished';
  winner: XiangqiPlayer | null;
  moves: XiangqiMove[];
  gameHistory: XiangqiGameState[];
  gameMode: GameMode;
  gameType: 'xiangqi';
  difficulty?: Difficulty;
  isAIThinking: boolean;
  // 中国象棋特有状态
  selectedPiece?: Position; // 当前选中的棋子
  possibleMoves?: XiangqiMove[]; // 选中棋子的可能移动
  isCheck?: boolean; // 是否将军
  isCheckmate?: boolean; // 是否将死
  isStalemate?: boolean; // 是否和棋
  capturedPieces: { black: XiangqiPiece[]; red: XiangqiPiece[] }; // 被吃掉的棋子
  moveCount: number; // 回合数
  repeatedMoves: number; // 重复移动次数（用于和棋判定）
  fiftyMoveRule: number; // 50回合规则计数

  // 棋盘特有信息
  redGeneralPos?: Position; // 红方将位置
  blackGeneralPos?: Position; // 黑方将位置

  // 游戏统计
  totalMoves: number; // 总移动数
  captureMoves: number; // 吃子移动数
  checkMoves: number; // 将军次数
}

// 中国象棋棋子等级映射（用于AI评估）
export const XIANGQI_PIECE_VALUES: Record<XiangqiPieceType, number> = {
  general: 10000,  // 将/帅（无价之宝）
  advisor: 20,     // 士
  elephant: 20,    // 象/相
  horse: 40,       // 马
  chariot: 90,     // 车
  cannon: 45,      // 炮
  soldier: 10      // 兵/卒
};

// 中国象棋棋子中文名称
export const XIANGQI_PIECE_NAMES: Record<XiangqiPieceType, { red: string; black: string }> = {
  general: { red: '帅', black: '将' },
  advisor: { red: '仕', black: '士' },
  elephant: { red: '相', black: '象' },
  horse: { red: '马', black: '马' },
  chariot: { red: '车', black: '车' },
  cannon: { red: '炮', black: '砲' },
  soldier: { red: '兵', black: '卒' }
};

// 中国象棋棋子符号
export const XIANGQI_PIECE_SYMBOLS: Record<XiangqiPieceType, { red: string; black: string }> = {
  general: { red: '帥', black: '將' },
  advisor: { red: '仕', black: '士' },
  elephant: { red: '相', black: '象' },
  horse: { red: '馬', black: '馬' },
  chariot: { red: '車', black: '車' },
  cannon: { red: '炮', black: '砲' },
  soldier: { red: '兵', black: '卒' }
};

// 中国象棋棋子表情符号
export const XIANGQI_PIECE_EMOJIS: Record<XiangqiPieceType, { red: string; black: string }> = {
  general: { red: '👑', black: '🎭' },
  advisor: { red: '🛡️', black: '🛡️' },
  elephant: { red: '🐘', black: '🐘' },
  horse: { red: '🐴', black: '🐴' },
  chariot: { red: '🚗', black: '🚙' },
  cannon: { red: '💣', black: '💥' },
  soldier: { red: '♟️', black: '♟️' }
};

// 中国象棋配置 (9×10棋盘)
export const XIANGQI_CONFIG: GameConfig = {
  boardSize: 9,      // 9×10棋盘（9列10行）
  initialEnergy: 0,   // 不使用能量系统
  energyPerTurn: 0,
  winCondition: 0,    // 将死对方将/帅
  gameType: 'xiangqi'
};

// ==================== 虎鸡虫棒棋相关接口 ====================

// 虎鸡虫棒棋棋子类型
export type TigerChickenPieceType = 'tiger' | 'chicken' | 'bug' | 'stick';

// 虎鸡虫棒棋棋子
export interface TigerChickenPiece {
  type: TigerChickenPieceType;
  player: Player;
  hasMoved?: boolean;
}

// 虎鸡虫棒棋移动
export interface TigerChickenMove {
  player: Player;
  from: Position;
  to: Position;
  captures?: TigerChickenPiece[]; // 被吃的棋子
  isCapture?: boolean; // 是否吃子
  isWinningMove?: boolean; // 是否是获胜移动
}

// 虎鸡虫棒棋游戏状态
export interface TigerChickenGameState {
  // 基础游戏状态
  board: (TigerChickenPiece | null)[][];
  currentPlayer: Player;
  status: 'playing' | 'finished';
  winner: Player | null;
  moves: TigerChickenMove[];
  gameHistory: TigerChickenGameState[];
  gameMode: GameMode;
  gameType: 'tigerchicken';
  difficulty?: Difficulty;
  isAIThinking: boolean;

  // 虎鸡虫棒棋特有状态
  selectedPiece?: Position; // 当前选中的棋子
  possibleMoves?: TigerChickenMove[]; // 选中棋子的可能移动
  capturedPieces: { black: TigerChickenPiece[]; white: TigerChickenPiece[] }; // 被吃掉的棋子

  // 大本营位置
  blackHome: Position; // 黑方大本营 (底边中点)
  whiteHome: Position; // 白方大本营 (顶边中点)

  // 游戏统计
  totalMoves: number;
  captureMoves: number;
  winningMoves: number;
}

// 虎鸡虫棒棋棋子等级映射
export const TIGER_CHICKEN_PIECE_VALUES: Record<TigerChickenPieceType, number> = {
  tiger: 10,     // 虎
  chicken: 6,   // 鸡
  bug: 4,        // 虫
  stick: 8       // 棒
};

// 虎鸡虫棒棋棋子中文名称
export const TIGER_CHICKEN_PIECE_NAMES: Record<TigerChickenPieceType, string> = {
  tiger: '虎',
  chicken: '鸡',
  bug: '虫',
  stick: '棒'
};

// 虎鸡虫棒棋棋子符号
export const TIGER_CHICKEN_PIECE_SYMBOLS: Record<TigerChickenPieceType, { black: string; white: string }> = {
  tiger: { black: '虎', white: '虎' },
  chicken: { black: '鸡', white: '鸡' },
  bug: { black: '虫', white: '虫' },
  stick: { black: '棒', white: '棒' }
};

// 虎鸡虫棒棋棋子表情符号
export const TIGER_CHICKEN_PIECE_EMOJIS: Record<TigerChickenPieceType, { black: string; white: string }> = {
  tiger: { black: '🐅', white: '🐅' },
  chicken: { black: '🐔', white: '🐔' },
  bug: { black: '🐛', white: '🐛' },
  stick: { black: '🎋', white: '🎋' }
};

// 虎鸡虫棒棋配置 (7×7棋盘)
export const TIGER_CHICKEN_CONFIG: GameConfig = {
  boardSize: 7,      // 7×7棋盘
  initialEnergy: 0,   // 不使用能量系统
  energyPerTurn: 0,
  winCondition: 0,    // 占领对方大本营
  gameType: 'tigerchicken'
};

// ==================== 井字棋类型定义 ====================

// 井字棋棋盘大小类型
export type TicTacToeBoardSize = 3 | 5 | 7;

// 井字棋移动信息
export interface TicTacToeMove {
  position: Position;
  player: Player;
  timestamp: number;
}

// 井字棋游戏状态
export interface TicTacToeGameState {
  board: (Player | null)[][];
  currentPlayer: Player;
  status: 'playing' | 'finished';
  winner: Player | null;
  moves: TicTacToeMove[];
  gameHistory: TicTacToeGameState[];
  gameMode: GameMode;
  gameType: 'tictactoe';
  difficulty?: Difficulty;
  isAIThinking: boolean;

  // 井字棋特有状态
  boardSize: TicTacToeBoardSize;  // 棋盘大小
  winLength: number;             // 连线长度获胜条件（3x3为3，5x5和7x7为4）
  totalMoves: number;
  lastMove?: Position;           // 最后一步位置
  winningLine?: Position[];      // 获胜连线位置
}

// 井字棋配置
export const TICTACTOE_CONFIGS: Record<TicTacToeBoardSize, GameConfig> = {
  3: {
    boardSize: 3,
    initialEnergy: 0,
    energyPerTurn: 0,
    winCondition: 3,  // 三连获胜
    gameType: 'tictactoe'
  },
  5: {
    boardSize: 5,
    initialEnergy: 0,
    energyPerTurn: 0,
    winCondition: 4,  // 四连获胜
    gameType: 'tictactoe'
  },
  7: {
    boardSize: 7,
    initialEnergy: 0,
    energyPerTurn: 0,
    winCondition: 4,  // 四连获胜
    gameType: 'tictactoe'
  }
};

// ==================== 四子棋相关接口 ====================
export interface ConnectFourMove {
  position: Position;
  player: Player;
  timestamp: number;
}

export interface ConnectFourGameState {
  board: (Player | null)[][];
  currentPlayer: Player;
  status: GameStatus;
  winner: Player | null;
  moves: ConnectFourMove[];
  gameHistory: ConnectFourGameState[];
  lastMove?: Position;
  isAIThinking?: boolean;
  gameMode: GameMode;
  gameType: 'connectfour';
  difficulty?: Difficulty;
}

// 四子棋配置
export const CONNECTFOUR_CONFIG: GameConfig = {
  boardSize: 8,
  initialEnergy: 0,
  energyPerTurn: 0,
  winCondition: 4,
  gameType: 'connectfour'
};

// ==================== Hnefatafl（维京板棋）相关接口 ====================

// Hnefatafl棋子类型
export type HnefataflPieceType = 'king' | 'warrior' | 'taflman';

// Hnefatafl棋子
export interface HnefataflPiece {
  type: HnefataflPieceType;
  player: 'attacker' | 'defender'; // attacker: 进攻方, defender: 防守方
  hasMoved?: boolean;
}

// Hnefatafl移动
export interface HnefataflMove {
  player: 'attacker' | 'defender';
  from: Position;
  to: Position;
  captures?: HnefataflPiece[]; // 被夹吃的棋子
  isCapture?: boolean; // 是否是夹吃移动
  isWinningMove?: boolean; // 是否是获胜移动
  isCornerEscape?: boolean; // 是否是角落逃脱（防守方获胜）
  score?: number; // AI评估分数
}

// Hnefatafl游戏状态
export interface HnefataflGameState {
  // 基础游戏状态
  board: (HnefataflPiece | null)[][];
  currentPlayer: 'attacker' | 'defender';
  status: 'playing' | 'finished';
  winner: 'attacker' | 'defender' | null;
  moves: HnefataflMove[];
  gameHistory: HnefataflGameState[];
  gameMode: GameMode;
  gameType: 'hnefatafl';
  difficulty?: Difficulty;
  isAIThinking?: boolean;

  // Hnefatafl特有状态
  selectedPiece?: Position; // 当前选中的棋子
  possibleMoves?: HnefataflMove[]; // 选中棋子的可能移动
  capturedPieces: { attacker: HnefataflPiece[]; defender: HnefataflPiece[] }; // 被吃掉的棋子

  // 特殊位置
  kingPosition?: Position; // 国王当前位置
  corners: Position[]; // 四个角落位置

  // 游戏统计
  totalMoves: number;
  captureMoves: number;
  cornerEscapeAttempts: number; // 国王试图逃向角落的次数

  // 禁止位置
  forbiddenPositions: Position[]; // 禁止停留的位置（中心和四个角落）

  // 游戏结果
  kingCaptured?: boolean; // 国王是否被捕获（进攻方获胜）
  kingEscaped?: boolean; // 国王是否成功逃脱到角落（防守方获胜）
}

// Hnefatafl棋子等级映射（用于AI评估）
export const HNEFATAFL_PIECE_VALUES: Record<HnefataflPieceType, number> = {
  king: 10000,    // 国王（无价之宝）
  warrior: 50,    // 防守方兵卒
  taflman: 30     // 进攻方兵卒
};

// Hnefatafl棋子中文名称
export const HNEFATAFL_PIECE_NAMES: Record<HnefataflPieceType, { attacker: string; defender: string }> = {
  king: { attacker: '国王', defender: '国王' },
  warrior: { attacker: '勇士', defender: '兵卒' },
  taflman: { attacker: '战士', defender: '兵卒' }
};

// Hnefatafl棋子符号
export const HNEFATAFL_PIECE_SYMBOLS: Record<HnefataflPieceType, { attacker: string; defender: string }> = {
  king: { attacker: '👑', defender: '👑' },
  warrior: { attacker: '⚔', defender: '🛡️' },
  taflman: { attacker: '⚔', defender: '🛡️' }
};

// Hnefatafl棋子表情符号
export const HNEFATAFL_PIECE_EMOJIS: Record<HnefataflPieceType, { attacker: string; defender: string }> = {
  king: { attacker: '👑', defender: '👑' },
  warrior: { attacker: '⚔️', defender: '🛡️' },
  taflman: { attacker: '⚔️', defender: '🛡️' }
};

// Hnefatafl配置（11×11棋盘）
export const HNEFATAFL_CONFIG: GameConfig = {
  boardSize: 11,     // 11×11棋盘
  initialEnergy: 0,  // 不使用能量系统
  energyPerTurn: 0,
  winCondition: 0,    // 通过特定条件获胜
  gameType: 'hnefatafl'
};

// ==================== 直棋相关接口 ====================

// 直棋移动
export interface StraightMove {
  player: Player;
  from: Position;
  to: Position;
  captures?: StraightPiece[]; // 被吃的棋子
  isCapture?: boolean; // 是否吃子
  formedMill?: boolean; // 是否形成三子连线（成直）
  timestamp: number;
}

// 直棋棋子
export interface StraightPiece {
  player: Player;
  position: Position;
  isCaptured?: boolean;
}

// 直棋游戏状态
export interface StraightGameState {
  // 基础游戏状态
  board: (StraightPiece | null)[][];
  currentPlayer: Player;
  status: 'playing' | 'finished' | 'placing'; // placing: 布子阶段
  winner: Player | null;
  moves: StraightMove[];
  gameHistory: StraightGameState[];
  gameMode: GameMode;
  gameType: 'straight';
  difficulty?: Difficulty;
  isAIThinking?: boolean;

  // 直棋特有状态
  selectedPiece?: Position; // 当前选中的棋子
  possibleMoves?: Position[]; // 可能的移动位置
  capturedPieces: { black: StraightPiece[], white: StraightPiece[] }; // 被吃掉的棋子

  // 游戏阶段
  phase: 'placing' | 'moving' | 'flying'; // placing: 布子阶段, moving: 移动阶段, flying: 飞行阶段（剩余3枚棋子时）
  piecesToPlace: number; // 待放置的棋子数量
  blackPiecesOnBoard: number; // 黑方在棋盘上的棋子数
  whitePiecesOnBoard: number; // 白方在棋盘上的棋子数

  // 成直相关
  lastMill?: Position[]; // 最后形成的三子连线位置
  millFormations: { black: Position[][], white: Position[][] }; // 双方已形成的成直位置
  capturedMills: { black: Position[][], white: Position[][] }; // 双方已经吃过子的连线位置（布子阶段不能重复吃子）
  pendingCapture?: boolean; // 是否有待执行的吃子操作
  canCapture?: boolean; // 当前是否可以吃子

  // 游戏统计
  totalMoves: number;
  captureMoves: number;
  millFormationsCount: number;

  // 胜利条件
  blackPiecesRemaining: number; // 黑方剩余棋子数
  whitePiecesRemaining: number; // 白方剩余棋子数
}

// 直棋棋盘上的有效位置（24个交叉点）
export interface StraightPosition {
  row: number;
  col: number;
  position: Position;
}

// 直棋配置
export const STRAIGHT_CONFIG: GameConfig = {
  boardSize: 7,      // 7×7虚拟棋盘，实际使用24个交叉点
  initialEnergy: 0,   // 不使用能量系统
  energyPerTurn: 0,
  winCondition: 0,    // 通过特殊条件获胜
  gameType: 'straight'
};

// 直棋棋子名称
export const STRAIGHT_PIECE_NAMES: Record<Player, string> = {
  black: '黑子',
  white: '白子'
};

// 直棋棋子符号
export const STRAIGHT_PIECE_SYMBOLS: Record<Player, string> = {
  black: '⚫',
  white: '⚪'
};

// 直棋棋子表情符号
export const STRAIGHT_PIECE_EMOJIS: Record<Player, string> = {
  black: '⚫',
  white: '⚪'
};

// ==================== 萨米板棋（Tablut）相关接口 ====================

// 萨米板棋棋子类型
export type TablutPieceType = 'king' | 'guard' | 'attacker';

// 萨米板棋棋子
export interface TablutPiece {
  type: TablutPieceType;
  player: 'defender' | 'attacker'; // defender: 护王方, attacker: 攻击方
  hasMoved?: boolean;
}

// 萨米板棋移动
export interface TablutMove {
  player: 'defender' | 'attacker';
  from: Position;
  to: Position;
  captures?: TablutPiece[]; // 被夹吃的棋子
  isCapture?: boolean; // 是否是夹吃移动
  isKingEscape?: boolean; // 是否是国王逃脱移动（护王方获胜）
  isKingCapture?: boolean; // 是否是捕获国王移动（攻击方获胜）
  timestamp: number;
}

// 萨米板棋游戏状态
export interface TablutGameState {
  // 基础游戏状态
  board: (TablutPiece | null)[][];
  currentPlayer: 'defender' | 'attacker';
  status: 'playing' | 'finished';
  winner: 'defender' | 'attacker' | null;
  moves: TablutMove[];
  gameHistory: TablutGameState[];
  gameMode: GameMode;
  gameType: 'tablut';
  difficulty?: Difficulty;
  isAIThinking?: boolean;

  // 萨米板棋特有状态
  selectedPiece?: Position; // 当前选中的棋子位置
  possibleMoves?: Position[]; // 当前可能的移动列表
  capturedPieces: { defender: TablutPiece[], attacker: TablutPiece[] }; // 被吃掉的棋子

  // 特殊位置
  kingPosition?: Position; // 国王当前位置
  corners: Position[]; // 四个角落位置（国王逃脱目标）
  throne: Position; // 国王王座位置（4,4）

  // 游戏统计
  totalMoves: number;
  captureMoves: number;
  kingEscapeAttempts: number; // 国王试图逃脱到角落的次数

  // 游戏结果
  kingEscaped?: boolean; // 国王是否成功逃脱到角落（护王方获胜）
  kingCaptured?: boolean; // 国王是否被捕获（攻击方获胜）
}

// 萨米板棋棋子等级映射（用于AI评估）
export const TABLUT_PIECE_VALUES: Record<TablutPieceType, number> = {
  king: 10000,    // 国王（无价之宝）
  guard: 50,      // 守卫
  attacker: 30    // 攻击者
};

// 萨米板棋棋子中文名称
export const TABLUT_PIECE_NAMES: Record<TablutPieceType, { defender: string; attacker: string }> = {
  king: { defender: '国王', attacker: '国王' },
  guard: { defender: '守卫', attacker: '守卫' },
  attacker: { defender: '攻击者', attacker: '攻击者' }
};

// 萨米板棋棋子符号
export const TABLUT_PIECE_SYMBOLS: Record<TablutPieceType, { defender: string; attacker: string }> = {
  king: { defender: '👑', attacker: '👑' },
  guard: { defender: '🛡️', attacker: '🛡️' },
  attacker: { defender: '⚔️', attacker: '⚔️' }
};

// 萨米板棋棋子表情符号
export const TABLUT_PIECE_EMOJIS: Record<TablutPieceType, { defender: string; attacker: string }> = {
  king: { defender: '👑', attacker: '👑' },
  guard: { defender: '🛡️', attacker: '🛡️' },
  attacker: { defender: '⚔️', attacker: '⚔️' }
};

// 萨米板棋配置（9×9棋盘）
export const TABLUT_CONFIG: GameConfig = {
  boardSize: 9,        // 9×9棋盘
  initialEnergy: 0,     // 不使用能量系统
  energyPerTurn: 0,
  winCondition: 0,      // 通过特定条件获胜
  gameType: 'tablut'
};

// 更新GameType类型以包含新游戏
export type UpdatedGameType = GameType | 'hnefatafl' | 'straight' | 'tablut';